import { createContext, useContext, useEffect, useState, useMemo } from "react";

const STORAGE_KEY = "faytex_cart_v1";

const CartContext = createContext(null);

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  function addItem(product, qty = 1) {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: Math.min(100, i.quantity + qty) } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price_ghs: Number(product.price_ghs) || 0,
          image: product.image,
          color: product.color,
          quantity: Math.max(1, qty),
        },
      ];
    });
  }

  function updateQty(product_id, quantity) {
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === product_id ? { ...i, quantity: Math.max(0, quantity) } : i))
        .filter((i) => i.quantity > 0)
    );
  }

  function removeItem(product_id) {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }

  function clear() { setItems([]); }

  /**
   * Reconcile the local cart with fresh server-side pricing.
   * `refreshed` is a list of `{product_id, name?, price_ghs?, image?, color?, quantity?, removed?}`.
   * Returns a small delta object so the UI can toast changes.
   */
  function syncItems(refreshed) {
    let priceChanges = 0;
    const removed = [];
    setItems((prev) => {
      const byId = Object.fromEntries(refreshed.map((r) => [r.product_id, r]));
      const next = [];
      for (const item of prev) {
        const r = byId[item.product_id];
        if (!r || r.removed) {
          removed.push(item.name);
          continue;
        }
        const merged = { ...item };
        if (typeof r.price_ghs === "number" && r.price_ghs !== item.price_ghs) {
          merged.price_ghs = r.price_ghs;
          priceChanges += 1;
        }
        if (r.name) merged.name = r.name;
        if (r.image) merged.image = r.image;
        if (r.color) merged.color = r.color;
        next.push(merged);
      }
      return next;
    });
    return { priceChanges, removed };
  }

  const totals = useMemo(() => {
    const count = items.reduce((a, i) => a + i.quantity, 0);
    const subtotal = items.reduce((a, i) => a + i.quantity * i.price_ghs, 0);
    return { count, subtotal };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, syncItems, ...totals }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
